import React, {type CSSProperties, PropsWithChildren, ReactElement, useRef, useState, useEffect} from "react"
import {GatsbyImage, getImage, IGatsbyImageData, withArtDirection} from "gatsby-plugin-image";
import * as styles from "./styles.module.scss"
import cls from "classnames";
import { useLayout } from "src/hooks/useLayout";

type StaticImageNode =
    ReactElement &
    {
        props: {
            "__imageData": IGatsbyImageData,
            [key: string]: any
        }
    }

export type BackgroundVideoProps = {
    lgDesktop?: string,
    desktop: string,
    poster?: string,
    autoPlay?: boolean,
    loop?: boolean,
    muted?: boolean,
    playsInline?: boolean,
}

export type BackgroundImageProps = {
    alt: string
    lgDesktop?: StaticImageNode,
    desktop?: StaticImageNode,
    tablet?: StaticImageNode,
    mobile?: StaticImageNode,
    className?: string,
    objectFit?: CSSProperties["objectFit"];
    objectPosition?: CSSProperties["objectPosition"];
    childrenClassnames?: {
        content?: string
    }
    bgClassName?: string,
    video?: BackgroundVideoProps,

    parallax?: {
        enabled: boolean,
        speed?: number,
        maxShift?: number,
        position?: {
            top?: string,
            bottom?: string
        }
    }
}

const LG_DESKTOP_QUERY = "(min-width: 1601px)"

export const BackgroundImage = ({
                                    lgDesktop,
                                    desktop,
                                    tablet,
                                    mobile,
                                    className,
                                    alt,
                                    bgClassName,
                                    children,
                                    childrenClassnames,
                                    objectFit,
                                    objectPosition,
                                    parallax,
                                    video
                                    }: PropsWithChildren<BackgroundImageProps>) => {

    const { isDesktop, isTablet, isPhone } = useLayout();
    const isDesktopOnly = isDesktop && !isTablet && !isPhone

    const wrapperRef = useRef<HTMLDivElement>(null)
    const videoRef = useRef<HTMLVideoElement>(null)
    const [offset, setOffset] = useState(0)
    const [isLgDesktop, setIsLgDesktop] = useState(false)

    useEffect(() => {
        if (typeof window === "undefined") return

        const mq = window.matchMedia(LG_DESKTOP_QUERY)
        const update = () => setIsLgDesktop(mq.matches)

        update()
        mq.addEventListener("change", update)
        return () => mq.removeEventListener("change", update)
    }, [])

    useEffect(() => {
        if (!parallax?.enabled) return

        const handleScroll = () => {
            if (!wrapperRef.current) return
            const rect = wrapperRef.current.getBoundingClientRect()
            const windowHeight = window.innerHeight

            if (rect.top < windowHeight && rect.bottom > 0) {
            const visibleProgress = Math.min(
                Math.max(0, (windowHeight - rect.top) / (windowHeight + rect.height)),
                1
            )
            setOffset(visibleProgress)
            }
        }

        handleScroll()
        window.addEventListener("scroll", handleScroll, { passive: true })
        return () => window.removeEventListener("scroll", handleScroll)
    }, [parallax?.enabled])

    const translateY = offset * (parallax?.speed ?? -200)

    const videoSrc = video && isDesktopOnly
        ? (isLgDesktop && video.lgDesktop) || video.desktop
        : undefined

    useEffect(() => {
        const el = videoRef.current
        if (!el || !videoSrc) return

        el.load()
        const playPromise = el.play()
        if (playPromise !== undefined) {
            playPromise.catch(() => {
                el.muted = true
                el.play().catch(() => {})
            })
        }
    }, [videoSrc])

    const artDirected = []

    if (lgDesktop && lgDesktop.props.__imageData) {
        artDirected.push({media: "(min-width: 1601px)", image: lgDesktop.props.__imageData})
    }
    if (mobile && mobile.props.__imageData) {
        artDirected.push({media: "(max-width: 767px)", image: mobile.props.__imageData})
    }
    if (tablet && tablet.props.__imageData) {
        artDirected.push({media: "(max-width: 1023px)", image: tablet.props.__imageData})
    }

    const images = useRef(
        desktop ? withArtDirection(desktop.props.__imageData, artDirected) : undefined
    ).current

    const renderBackground = () => {
        if (videoSrc) {
            return (
                <video
                    ref={videoRef}
                    className={cls(styles.bg, bgClassName)}
                    style={{
                        position: "absolute",
                        inset: 0,
                        width: "100%",
                        height: "100%",
                        objectFit: objectFit || "cover",
                        objectPosition: objectPosition || "center"
                    }}
                    poster={video?.poster}
                    autoPlay={video?.autoPlay ?? true}
                    loop={video?.loop ?? true}
                    muted={video?.muted ?? true}
                    playsInline={video?.playsInline ?? true}
                    preload="auto"
                >
                    <source src={videoSrc} />
                </video>
            )
        }

        if (!desktop || !desktop.props.__imageData || !images) return null

        if (parallax?.enabled && isDesktopOnly) {
            return (
                <div
                    className={cls(styles.bgWrapper, bgClassName)}
                    style={{
                        transform: `translateY(${translateY}px)`,
                        backgroundImage: `url(${getImage(images)?.images?.fallback?.src})`,
                        backgroundSize: objectFit || "cover",
                        backgroundPosition: objectPosition || "center",
                        backgroundRepeat: "no-repeat",
                        bottom: parallax?.position?.bottom,
                        top: parallax?.position?.top
                    }}
                />
            )
        }

        return (
            <GatsbyImage
                objectFit={objectFit}
                objectPosition={objectPosition}
                className={cls(styles.bg, bgClassName)}
                image={images}
                alt={alt}
            />
        )
    }

    return (
        <div
            className={cls(className, styles.wrapper, "gatsby-bg-image-wrapper")}
            ref={wrapperRef}
        >
            {renderBackground()}

            <div className={cls(styles.content, childrenClassnames?.content)}>
                {children}
            </div>
        </div>
    )
}